import React from 'react';
import { styles } from './MenuItem.css';
import { ArrowRight } from '@jbhi-fi/lanyard-ui/components/Icons';
import { MenuDivider } from '../MenuDivider/MenuDivider';
import { MenuSubheading } from '../MenuSubheading/MenuSubheading';
import { MenuDropdown } from '../MenuDropdown/MenuDropdown';
import { MenuItemType } from '../types';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { LinkIcon } from '../../LinkIcon/LinkIcon';
import { SHOP_ALL_TEXT } from '../constants';
import { Icon, IconName } from '@jbhi-fi/lanyard-ui/components/Icon';

export type MenuItemProps = {
  type: MenuItemType;
  label?: string;
  leftIcon?: React.ReactNode;
  shouldHideArrow?: boolean;
  count?: number;
  isBold?: boolean;
  isActive?: boolean;
  href?: string;
  shouldOpenInNewWindow?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onBack?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  subMenuItems?: Array<MenuItemProps>;
  trackingData?: { nesting?: Array<string | undefined> };
  isMainCollection?: boolean;
  isTopMenu?: boolean;
  isSnackbarVisible?: boolean;
};

const RightElement = ({
  count,
  shouldHideArrow,
  isActive,
  isMainCollection = false,
}: {
  count?: number;
  shouldHideArrow?: boolean;
  isActive?: boolean;
  isMainCollection?: boolean;
}) => {
  if (!count && shouldHideArrow) return null;
  if (count) {
    return <div>{count}</div>;
  }

  return !shouldHideArrow ? (
    <span className={styles.arrow({ active: isActive })}>
      <ArrowRight height="8px" width="8px" className={styles.arrowIcon({ isMainCollection })} />
    </span>
  ) : null;
};

const Wrapper = ({
  href,
  className,
  onClick,
  children,
  data_t,
  shouldOpenInNewWindow,
}: {
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  className: string;
  children: React.ReactNode;
  data_t?: string;
  shouldOpenInNewWindow?: boolean;
}) => {
  if (href) {
    return (
      <a
        role="menuitem"
        href={href}
        className={className}
        onClick={onClick}
        data-t={data_t}
        data-testid="navigation-menu-dropdown-item-link"
        target={shouldOpenInNewWindow ? '_blank' : undefined}
        rel={shouldOpenInNewWindow ? 'noreferrer' : undefined}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      role="menuitem"
      type="button"
      className={className}
      onClick={onClick}
      data-t={data_t}
      data-testid="navigation-menu-dropdown-item-button"
    >
      {children}
    </button>
  );
};

const MenuItemWrapper = ({ children, className }: { children: React.ReactNode; className: string }) => {
  return (
    <div className={className} data-testid="navigation-menu-dropdown-item-wrapper">
      {children}
    </div>
  );
};

export const MenuItem = ({
  type,
  label = '',
  leftIcon,
  isBold = false,
  count,
  shouldHideArrow = false,
  isActive = false,
  onClick,
  href,
  subMenuItems,
  onBack,
  trackingData,
  shouldOpenInNewWindow,
  isMainCollection = false,
  isTopMenu = false,
  isSnackbarVisible = false,
}: MenuItemProps) => {
  const menuItemContent = (
    <Wrapper
      href={href}
      shouldOpenInNewWindow={shouldOpenInNewWindow}
      className={classNames([
        styles.button({
          fontWeight: isBold ? 'bold' : 'normal',
          active: isActive,
        }),
        styles.wrapper({ isTopMenu, active: isActive }),
      ])}
      onClick={onClick}
      data_t={label.replace(/\s/g, '')}
    >
      {leftIcon ? (
        <div className={styles.icon}>
          <Icon name={leftIcon as IconName} height={18} width={18} />
        </div>
      ) : null}
      <span className={styles.label({ isMainCollection: isMainCollection })}>{label}</span>
      {isMainCollection ? <span className={styles.shopAll}>{SHOP_ALL_TEXT}</span> : null}
      {isMainCollection ? (
        <LinkIcon className={styles.linkIcon} />
      ) : (
        <RightElement
          shouldHideArrow={!isMainCollection && shouldHideArrow}
          count={count}
          isActive={isActive}
          isMainCollection={isMainCollection}
        />
      )}
    </Wrapper>
  );

  if (
    type === MenuItemType.divider ||
    type === MenuItemType.headingAndDivider ||
    type === MenuItemType.dividerWithCollection
  ) {
    return (
      <MenuItemWrapper className={styles.menuItemWrapper({ isTopMenu })}>
        <MenuDivider isTopMenu={isTopMenu} />
        {type === MenuItemType.headingAndDivider ? <MenuSubheading label={label} /> : null}
        {type === MenuItemType.dividerWithCollection ? menuItemContent : null}
      </MenuItemWrapper>
    );
  }

  return (
    <li className={styles.menuItem} data-testid="navigation-menu-dropdown-item">
      {menuItemContent}
      {subMenuItems?.length ? (
        <div className={styles.subMenu} data-testid="navigation-menu-dropdown-sub-menu">
          <MenuDropdown
            menuItems={subMenuItems}
            isOpen={isActive}
            label={label}
            onBack={onBack}
            trackingData={{ ...trackingData, nesting: [...(trackingData?.nesting ?? []), label] }}
            isSnackbarVisible={isSnackbarVisible}
          />
        </div>
      ) : null}
    </li>
  );
};
